<template>
  <div v-if="!isEmptyData || pageLoading">
    <div class="executive-insights-report">
      <ExecutiveInsightsReportSummary
        :loading="pageLoading"
        :data="{ applicationsReceived, overallPassthroughRate, hiresMade }"
        :project-name="projectName"
        :date-range="dateRange"
        :tooltips="tooltips"
        class="summary"
      />
      <ExecutiveInsightsReportTimeToHireWidget
        :loading="pageLoading"
        :time-to-hire="timeToHire"
        :empty-data="isEmptyData"
        :error="error"
        class="benchmark"
      />
      <ExecutiveInsightsReportOfferAcceptanceWidget
        :loading="pageLoading"
        :offer-acceptance-rate="offerAcceptanceRate"
        :empty-data="isEmptyData"
        :error="error"
        class="benchmark"
      />
      <ExecutiveInsightsReportDropoffWidget
        :loading="pageLoading"
        :drop-off-rate="dropOffRate"
        :empty-data="isEmptyData"
        :error="error"
        :flags="flags"
        class="benchmark"
      />
      <ExecutiveInsightsReportGenderDistribution
        :loading="pageLoading"
        :gender-distribution="genderDistribution"
        :labels-for-loading="labelsForLoading"
        class="distribution"
      />
      <ExecutiveInsightsReportSourceDistribution
        :loading="pageLoading"
        :source-distribution="sourceDistribution"
        :labels-for-loading="labelsForLoading"
        class="distribution"
      />
    </div>
    <FilterExportPreview />
  </div>
  <NoDataForWidget v-else />
</template>

<script setup>
import {
  onMounted, onBeforeUnmount, computed,
} from 'vue';
import { useFilters } from '@/stores/filters/filters';
import { useRoute, useRouter } from 'vue-router';
import { useAppLoading } from '@/stores/app/loading';
import { storeToRefs } from 'pinia';
import { useDatePicker } from '@/stores/filters/datePicker';
import { useAppBase } from '@/stores/app/base';
import { subscribeToFilterUpdates, unsubscribeToFilterUpdates } from '@/helpers/filterDispatcher';
import NoDataForWidget from '@/components/insights/NoDataForWidget.vue';
import { useAppAuthData } from '@/stores/app/authData';
import { useTooltips } from '@/stores/insights/tooltips';
import { useExecutiveInsights } from '@/stores/insights/executiveInsights';
import ExecutiveInsightsReportSummary from '@/components/insights/ProcessInsights/ExecutiveInsightsReport/ExecutiveInsightsReportSummary.vue';
import ExecutiveInsightsReportTimeToHireWidget from '@/components/insights/ProcessInsights/ExecutiveInsightsReport/ExecutiveInsightsReportTimeToHireWidget.vue';
import ExecutiveInsightsReportOfferAcceptanceWidget from '@/components/insights/ProcessInsights/ExecutiveInsightsReport/ExecutiveInsightsReportOfferAcceptanceWidget.vue';
import ExecutiveInsightsReportDropoffWidget from '@/components/insights/ProcessInsights/ExecutiveInsightsReport/ExecutiveInsightsReportDropoffWidget.vue';
import ExecutiveInsightsReportGenderDistribution from '@/components/insights/ProcessInsights/ExecutiveInsightsReport/ExecutiveInsightsReportGenderDistribution.vue';
import ExecutiveInsightsReportSourceDistribution from '@/components/insights/ProcessInsights/ExecutiveInsightsReport/ExecutiveInsightsReportSourceDistribution.vue';
import { useInsightsExport } from '@/stores/insights/export';
import { useInsightsShare } from '@/stores/insights/share';
import FilterExportPreview from '@/components/insights/ExportModal/FilterExportPreview.vue';

const emit = defineEmits(['track-page-load']);

const {
  timeToHire,
  applicationsReceived,
  overallPassthroughRate,
  hiresMade,
  offerAcceptanceRate,
  genderDistribution,
  sourceDistribution,
  labelsForLoading,
  dropOffRate,
  isEmptyData,
  error,
  flags,
} = storeToRefs(useExecutiveInsights());

const { pageLoading } = storeToRefs(useAppLoading());
const { authData } = storeToRefs(useAppAuthData());
const { tooltips } = storeToRefs(useTooltips());
const { dateRange } = storeToRefs(useDatePicker());

const { loadFilters, setFilterConfig } = useFilters();
const { setDatePickerVariant, resetDateRange } = useDatePicker();
const { loadExecutiveInsights } = useExecutiveInsights();
const { showFilterBarSettings } = useAppBase();
const { setExportConfig, resetExportConfig } = useInsightsExport();
const { setShareConfig, resetShareConfig } = useInsightsShare();

const route = useRoute();
const router = useRouter();

const projectName = computed(() => authData.value.projectName);

const loadPage = async (params) => Promise.all([
  loadFilters(params),
  loadExecutiveInsights(params),
]);

onMounted(async () => {
  subscribeToFilterUpdates(loadPage);

  emit('track-page-load', { page: 'Executive Insights' });

  setFilterConfig({
    module: 'insights',
    endpoint: 'process',
    datepicker: true,
    narrow: true,
    showMyJobs: true,
  });

  setDatePickerVariant('multiOption');
  showFilterBarSettings({ route: route.name, options: [] });

  setShareConfig({
    shareable: true,
    showReportImageButton: true,
  });

  setExportConfig({
    csvExport: false,
    pngExport: true,
    pngWidgetOptions: {
      'applications-received': 'Metric: Applications Received',
      'overall-pass-through-rate': 'Metric: Pass-through Rate',
      'hires-made': 'Metric: Hires Made',
      'time-to-hire-benchmark': 'Benchmark: Time to Hire',
      'offer-acceptance-rate-benchmark': 'Benchmark: Offer Acceptance Rate',
      'drop-off-rate-benchmark': 'Benchmark: Drop-off Rate',
      'exec-gender-distribution': 'Graph: Gender Distribution',
      'source-distribution': 'Graph: Source Distribution',
    },
  });

  await loadPage({ routeQuery: route.query || '', selectedMyJobs: route.query?.owned === 'true', router });
});

onBeforeUnmount(() => {
  unsubscribeToFilterUpdates(loadPage);

  resetDateRange();

  resetExportConfig();

  resetShareConfig();
});
</script>

<style scoped lang="less">
.executive-insights-report {
  width: 100%;
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  grid-template-rows: 140px 211px 333px;
  gap: 16px;
  padding-bottom: 16px;
}

.summary {
  grid-column: 1 / -1;
  grid-row: 1;
}

.benchmark {
  grid-column: span 2;
  grid-row: 2;
}

.distribution {
  grid-column: span 3;
  grid-row: 3;
}
</style>
